import { computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import { formatName } from '../../../../utils/helper';
import BaseEmployeeStore from '../../../../shared/stores/baseEmployeeStore';
import OrderOverviewStore from './OrderOverviewStore';

interface Employee {
    id: number;
    firstname: string;
    lastname: string;
}

/**
 * The details of a current order
 */
class OrderViewStore extends BaseEmployeeStore {
    public orderOverviewEmployeeStore: OrderOverviewStore;

    public orderId: number | null = null;
    public orderCreated: number | null = null;
    public orderDelievered: number | null = null;
    public orderEmployee: Employee | null = null;
    public orderStatus: string | null = null;
    public orderObjectName: string | null = null;
    public orderAssignedProducts: Map<number, any> = new Map();
    public s3url: string = '';

    constructor(sdk: ApplicationSdk, orderOverviewEmployeeStore: OrderOverviewStore) {
        super(sdk);
        this.orderOverviewEmployeeStore = orderOverviewEmployeeStore;
        makeObservable(this, {
            orderId: observable,
            orderEmployee: observable,
            orderCreated: observable,
            orderObjectName: observable,
            orderDelievered: observable,
            orderCreatedDate: computed,
            orderDelieveredDate: computed,
            s3url: observable,
            orderAssignedProducts: observable,
            orderStatus: observable,
            status: computed,
            chargeableProducts: computed,
            freeProducts: computed,
            orderIdPadded: computed,
        });

        this.sdk = sdk;
        this.orderEmployee = null;
    }

    protected async onInit(): Promise<void> {
        this.orderOverviewEmployeeStore.setMenuConfig({
            customHeader: {
                title: 'Bestellung',
                icon: 'pi pi-truck',
            },
            backButton: {
                visible: true,
                disabled: false,
                customAction: async () => {
                    if (await this.onBeforeNavigateBack()) {
                        this.orderOverviewEmployeeStore.openOverviewOrder(false);
                    }
                },
            },
        });

        // this.orderOverviewEmployeeStore.updateFooterButton({
        //     label: 'Zurück', icon: 'pi pi-arrow-left', onClick: () => this.orderOverviewEmployeeStore.closeCurrentWindow()
        // });

        this.fetchOrder();
    }

    get chargeableProducts(): any[] {
        return Array.from(this.orderAssignedProducts.values())
            .filter((product: any) => product.chargeable)
            .map((item: any) => ({
                ...item,
                image: item.image ? `${this.s3url}${item.image}.jpeg` : null,
            }));
    }

    get freeProducts(): any[] {
        return Array.from(this.orderAssignedProducts.values())
            .filter((product: any) => !product.chargeable)
            .map((item: any) => ({
                ...item,
                image: item.image ? `${this.s3url}${item.image}.jpeg` : null,
            }));
    }

    get objectName(): string {
        return this.orderObjectName || '';
    }

    get employeeName(): string {
        if (this.orderEmployee) {
            return formatName(this.orderEmployee.firstname, this.orderEmployee.lastname);
        }
        return 'Unbekannt';
    }

    get id(): string {
        return 'order';
    }

    get orderIdPadded(): string {
        return `${this.orderId}`.padStart(5, '0');
    }

    get status(): string {
        return this.orderStatus || '';
    }

    get orderCreatedDate(): any {
        return this.orderCreated ? new Date(this.orderCreated * 1000) : null;
    }

    get orderDelieveredDate(): any {
        return this.orderDelievered ? new Date(this.orderDelievered * 1000) : null;
    }

    get isBackButtonVisible(): boolean {
        return true;
    }

    get isFooterButtonVisible(): boolean {
        return false;
    }

    get isFooterButtonEnabled(): boolean {
        return false;
    }

    get isFooterButtonLoading(): boolean {
        return this.isLoading;
    }

    get footerButtonHelpText(): string {
        return '';
    }

    public async fetchOrder(): Promise<void> {
        runInAction(() => {
            this.isLoading = true;
        });

        const orderDetails: any = await this.sdk.fetchApi('order', 'get', {
            order_id: this.orderId,
        });

        runInAction(() => {
            this.isLoading = false;
            if (orderDetails?.status === 200) {
                const orderData = orderDetails.json;

                if (orderData?.order?.employee_id) {
                    this.orderEmployee = {
                        id: orderData.order.employee_id,
                        firstname: orderData.order.employee_firstname,
                        lastname: orderData.order.employee_lastname,
                    };
                }

                this.orderObjectName = orderData?.order?.object_name || '';
                this.orderStatus = orderData?.order?.status || 'NEW';
                this.orderDelievered = orderData?.order?.delievered_utc || null;
                this.orderCreated = orderData?.order?.created_utc || null;

                if (orderData?.products) {
                    orderData.products.forEach((product: any) => {
                        // Add the product to the assigned products
                        if (!this.orderAssignedProducts.has(product.id)) {
                            this.orderAssignedProducts.set(product.id, product);
                        }
                    });
                }
                this.s3url = orderData?.s3url || '';
            }
        });
    }

    async onBeforeNavigateBack(): Promise<boolean> {
        return true;
    }
}

export default OrderViewStore;
